<template>
  <v-col class="pa-6">
    <div v-if="!creatingAd && !viewingRequest">
      <v-row class="mt-5 px-6" align="center">
        <v-col cols="12" md="3">
          <v-text-field
            class="input ma-0 pa-0"
            label="Search..."
            prepend-inner-icon="mdi-magnify"
            v-model="search"
            rounded
            :hide-details="true"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-row class="ma-0" justify="center" justify-md="start">
            <v-select
              style="max-width: 240px"
              class="input ma-0 pa-0"
              label="Status Type"
              v-model="selectedType"
              :items="statusTypes"
              rounded
              :hide-details="true"
              clearable
            ></v-select>
          </v-row>
        </v-col>
        <v-col cols="12" md="3">
          <v-row class="ma-0">
            <v-btn
              @click="enterCreateMode()"
              color="#e022d1"
              dark
              rounded
              width="130px"
              class="pink-btn"
            >Create Ad</v-btn>
          </v-row>
        </v-col>
      </v-row>

      <v-data-table
        class="elevation-1 mt-4 ml-4 mr-8"
        :items="filteredRequests"
        :headers="headers"
        :search="search"
      >
        <template v-slot:item.action="{ item }">
          <td class="d-flex align-center">
            <v-icon color="#4390c0" class="mr-3" @click="viewRequest(item)">mdi-eye</v-icon>
            <v-icon
              v-if="role == 'Super Admin'"
              color="#4390c0"
              @click="selectedRequest = item; deletingAd = true"
            >mdi-delete</v-icon>
          </td>
        </template>
      </v-data-table>
    </div>

    <v-col
      v-if="creatingAd"
      class="pa-6 d-flex flex-column fill-height"
      style="background-color: #f5f7fa"
    >
      <v-row align="center" class="mb-6">
        <v-icon
          size="32"
          color="#4390c0"
          class="mr-3 cursor-pointer"
          @click="creatingAd = false"
        >mdi-arrow-left-circle</v-icon>
        <h2 class="text-h5 font-weight-medium">Create New Ad</h2>
      </v-row>

      <v-card flat class="pa-6 rounded-xl elevation-2" color="white">
        <v-row class="mb-8" justify="center">
          <v-col cols="12" md="6" class="d-flex justify-center">
            <v-hover v-slot="{ isHovering, props }">
              <v-card
                v-bind="props"
                class="d-flex align-center justify-center position-relative cursor-pointer"
                :style="{
                  border: '2px dashed #cfd8dc',
                  backgroundColor: '#fafafa',
                  height: '250px',
                  width: '250px',
                  transition: '0.2s',
                  boxShadow: isHovering ? '0 0 0 2px #4390c0 inset' : 'none',
                }"
                rounded="xl"
                @click="triggerFileInput"
              >
                <template v-if="!previewURL">
                  <div class="text-center">
                    <v-icon size="48" color="#9e9e9e">mdi-image-plus</v-icon>
                    <div class="mt-2 text-subtitle-2 grey--text">Click to upload</div>
                  </div>
                </template>

                <template v-else>
                  <v-img :src="previewURL" class="rounded-xl" height="100%" width="100%" cover></v-img>

                  <div
                    class="absolute inset-0 d-flex align-center justify-center"
                    style="
                      background: rgba(0, 0, 0, 0.35);
                      opacity: 0;
                      transition: 0.2s;
                    "
                    @mouseover="$event.currentTarget.style.opacity = 1"
                    @mouseleave="$event.currentTarget.style.opacity = 0"
                  >
                    <v-icon color="white" size="36">mdi-upload</v-icon>
                  </div>
                </template>

                <input
                  ref="fileInput"
                  type="file"
                  accept="image/*"
                  style="display: none"
                  @change="handleFileChange"
                />
              </v-card>
            </v-hover>
          </v-col>
        </v-row>

        <v-row class="mb-3" justify="center">
          <v-col cols="12" md="8">
            <v-text-field
              v-model="adTitle"
              :readonly="boostingAgain"
              label="Ad Title"
              placeholder="Enter your ad title"
              outlined
              clearable
              color="#4390c0"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row class="mb-6" justify="center">
          <v-col cols="12" md="8">
            <v-select
              v-model="selectedDoctor"
              :items="
                doctors.map((d) => ({
                  text: d.name + ' ' + d.surname,
                  value: d.id,
                }))
              "
              label="Select Doctor"
              outlined
              clearable
              dense
              :loading="loading"
              item-text="text"
              item-value="value"
            ></v-select>
          </v-col>
        </v-row>

        <v-row class="mb-6" justify="center">
          <v-col cols="12" md="8">
            <h3 class="text-subtitle-1 text-center mb-3 font-weight-medium">Select Ad Duration</h3>
            <v-card variant="tonal" color="grey-lighten-4" class="pa-4 rounded-lg">
              <v-radio-group v-model="selectedDuration">
                <v-row dense>
                  <v-col v-for="option in adDurations" :key="option.label" cols="12" class="py-1">
                    <v-radio :value="option.value" :label="option.label"></v-radio>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-card>
          </v-col>
        </v-row>

        <v-row justify="center" class="mt-8">
          <v-btn
            @click="createNewAdd()"
            color="#e022d1"
            rounded="xl"
            size="large"
            width="240"
            class="elevation-3 white--text"
          >Create New Ad</v-btn>
        </v-row>
      </v-card>
    </v-col>

    <div v-if="viewingRequest">
      <v-row justify="center">
        <v-row class="ma-2" justify="center" justify-md="start">
          <v-col cols="12" md="6">
            <v-row class="mb-2">
              <v-icon class="black-color" @click="close()">mdi-arrow-left</v-icon>
              <h4 class="grey-color mx-4" style="font-weight: 500">Request Info</h4>
            </v-row>
          </v-col>
          <v-col v-if="selectedRequest.reviewed || role == 'Admin'" cols="12" md="6">
            <v-row class="mb-2" justify="center" justify-md="end">
              <v-btn
                v-if="submittingAd == false"
                @click="submittingAd = true"
                dark
                rounded
                color="#4390c0"
              >Review</v-btn>
              <v-btn
                v-if="submittingAd == true"
                @click="submittingAd = false"
                dark
                rounded
                color="#4390c0"
              >Back to Ad</v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-row>
      <v-row justify="center">
        <div class="white-card pa-8">
          <div v-if="!submittingAd">
            <v-row justify="start" class="ma-0">
              <h2 class="fs400 mb-4">Ad Info</h2>
              <v-chip
                class="status-chip mx-2 mt-1"
                style="text-transform: capitalize"
                :color="statusColor(selectedRequest.status || 'Pending')"
                dark
              >{{ selectedRequest.status || "Pending" }}</v-chip>
            </v-row>
            <v-row class="ma-0">
              <v-col cols="12">
                <v-row justify="start">
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Title:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.title"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Date Created:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.date"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Expiry Date:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.expiry"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Doctor:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.doctorFullName"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Email Address:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.doctorEmail"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Price:</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-text-field
                        readonly
                        filled
                        outlined
                        dense
                        rounded
                        v-model="selectedRequest.price"
                      ></v-text-field>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row class="ma-0">
                  <p>
                    <strong>Image:</strong>
                  </p>
                </v-row>
                <v-row justify="center" justify-md="start" class="ma-0">
                  <img :src="selectedRequest.imageUrl" class="image mb-6" />
                </v-row>
              </v-col>
            </v-row>
          </div>
          <div v-if="submittingAd">
            <v-row justify="start" class="ma-0">
              <h2 class="fs400 mb-4">Reviewing Ad</h2>
            </v-row>
            <v-row v-if="selectedRequest.reviewedBy" justify="start" class="ma-0">
              <h4 class="fs400 mb-4">Reviewed by:</h4>
              <h4 class="blue-color mb-4">{{ selectedRequest.reviewedBy }}</h4>
            </v-row>

            <div style="max-height: 60vh; overflow-y: scroll">
              <v-form ref="reviewForm">
                <v-row justify="start" class="ma-0">
                  <h2 class="fs400 mb-2">1. Advert Material Provided</h2>
                </v-row>
                <v-row justify="start" class="ma-0">
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Final Advert file provided(image/video/PDF)</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.finalFileProvided"
                        required
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.finalFileProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.finalFileProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.finalFileProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.finalFileProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.finalFileProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.finalFileProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Marketing text/caption</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.marketingTextProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.marketingTextProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.marketingTextProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.marketingTextProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.marketingTextProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.marketingTextProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        class="mt-2"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.marketingTextProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Practitioner Headshot(If included)</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.practitionerHeadshotProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.practitionerHeadshotShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.practitionerHeadshotShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.practitionerHeadshotShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.practitionerHeadshotShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.practitionerHeadshotShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.practitionerHeadshotNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Practice Logo(If included)</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.practiceLogoProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.practiceLogoProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.practiceLogoProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.practiceLogoProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.practiceLogoProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.practiceLogoProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.practiceLogoProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Proof of rights to use image/media</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.proofOfRightsProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.proofOfRightsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.proofOfRightsProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.proofOfRightsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.proofOfRightsProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.proofOfRightsProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.proofOfRightsProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row justify="start" class="ma-0">
                  <h2 class="fs400 my-2">2. Content Compliance Checklist</h2>
                </v-row>
                <v-row justify="start" class="ma-0">
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Advert does not promise guaranteed results</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.guaranteedResultsProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.guaranteedResultsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.guaranteedResultsProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.guaranteedResultsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.guaranteedResultsProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="adReview.guaranteedResultsProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.guaranteedResultsProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No misleading, exaggerated, or false claims</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.notMisleadingProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.notMisleadingProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notMisleadingProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.notMisleadingProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notMisleadingProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.notMisleadingProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.notMisleadingProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          No comparison with other practitioners (HPCSA
                          compliance)
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.noComparisonProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.noComparisonProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.noComparisonProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.noComparisonProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.noComparisonProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.noComparisonProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.noComparisonProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No unlicensed products/services promoted</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.notUnlicensedProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.notUnlicensedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notUnlicensedProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.notUnlicensedProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notUnlicensedProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.notUnlicensedProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.notUnlicensedProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          No testimonials from patients (HPCSA
                          restricted)
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.noTestimonialsProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.noTestimonialsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.noTestimonialsProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.noTestimonialsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.noTestimonialsProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="adReview.noTestimonialsProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.noTestimonialsProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          No pricing that could be seen as a low-cost
                          inducement
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.lowInducementProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.lowInducementProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.lowInducementProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.lowInducementProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.lowInducementProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.lowInducementProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.lowInducementProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>All medical terms used correctly</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.termsCorrectProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.termsCorrectProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.termsCorrectProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.termsCorrectProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.termsCorrectProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.termsCorrectProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.termsCorrectProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Contact information accurate</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.contactAccurateProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.contactAccurateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.contactAccurateProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.contactAccurateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.contactAccurateProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="adReview.contactAccurateProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.contactAccurateProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No copyright or trademark violations</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.copyrightViolationsProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.copyrightViolationsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.copyrightViolationsProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.copyrightViolationsProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.copyrightViolationsProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="adReview.copyrightViolationsProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.copyrightViolationsProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Advert aligns with platform rules</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.alignsRulesProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.alignsRulesProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.alignsRulesProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.alignsRulesProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.alignsRulesProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.alignsRulesProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.alignsRulesProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row justify="start" class="ma-0">
                  <h2 class="fs400 my-2">3. Creative and Quality Review</h2>
                </v-row>
                <v-row justify="start" class="ma-0">
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>
                          High-quality resolution suitable for social/media
                          use
                        </strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.highQualityProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.highQualityProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.highQualityProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.highQualityProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.highQualityProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.highQualityProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.highQualityProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Layout is clean and professional</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.layoutCleanProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.layoutCleanProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.layoutCleanProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.layoutCleanProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.layoutCleanProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.layoutCleanProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.layoutCleanProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Text is readable and properly formatted</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.textReadableProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.textReadableProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.textReadableProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.textReadableProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.textReadableProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.textReadableProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.textReadableProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No inappropriate content or imagery</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.notInappropriateProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.notInappropriateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notInappropriateProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.notInappropriateProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notInappropriateProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row
                      v-show="adReview.notInappropriateProvidedShow"
                      justify="start"
                      class="ma-0"
                    >
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.notInappropriateProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                </v-row>

                <v-row justify="start" class="ma-0">
                  <h2 class="fs400 my-2">4. Risk & Ethics Review</h2>
                </v-row>
                <v-row justify="start" class="ma-0">
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>Ethical and respectful communication</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.ethicalProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.ethicalProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.ethicalProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.ethicalProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.ethicalProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.ethicalProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.ethicalProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No sensitive patient information displayed</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.notSensitiveProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.notSensitiveProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notSensitiveProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.notSensitiveProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notSensitiveProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.notSensitiveProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.notSensitiveProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No harmful suggestions or unsafe advice</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.notHarmfulProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.notHarmfulProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notHarmfulProvidedShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.notHarmfulProvidedShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notHarmfulProvidedShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.notHarmfulProvidedShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.notHarmfulProvidedNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                  <v-col class="py-0" cols="12" md="6">
                    <v-row justify="start" class="ma-0">
                      <p>
                        <strong>No inappropriate content or imagery</strong>
                      </p>
                    </v-row>
                    <v-row justify="start" class="ma-0">
                      <v-radio-group
                        class="mt-0"
                        row
                        v-model="adReview.notInappropriateRiskProvided"
                        :rules="nullRules"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                      >
                        <v-radio value="Yes" label="Yes"></v-radio>
                        <v-radio value="No" label="No"></v-radio>
                      </v-radio-group>
                      <h4
                        v-if="!adReview.notInappropriateRiskShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notInappropriateRiskShow = true;
                          $forceUpdate();
                        "
                      >Show Notes</h4>
                      <h4
                        v-if="adReview.notInappropriateRiskShow"
                        class="blue-color pointer mt-1"
                        @click="
                          adReview.notInappropriateRiskShow = false;
                          $forceUpdate();
                        "
                      >Hide Notes</h4>
                    </v-row>
                    <v-row v-show="adReview.notInappropriateRiskShow" justify="start" class="ma-0">
                      <v-textarea
                        class="mt-2"
                        :readonly="role != 'Admin' || selectedRequest.reviewed"
                        filled
                        outlined
                        dense
                        rounded
                        v-model="adReview.notInappropriateRiskNote"
                        label="Notes"
                      ></v-textarea>
                    </v-row>
                  </v-col>
                </v-row>
                <v-row
                  v-if="role == 'Admin' && !selectedRequest.reviewed"
                  justify="start"
                  class="ma-0"
                >
                  <v-btn class="mt-4" @click="submitReview()" dark rounded color="#4390c0">Submit</v-btn>
                </v-row>
                <div v-if="role == 'Super Admin' && selectedRequest.reviewed">
                  <v-row justify="center" justify-md="start" class="ma-0">
                    <p>
                      <strong>Ad Status:</strong>
                    </p>
                  </v-row>
                  <v-row justify="center" justify-md="start" class="ma-0">
                    <v-select
                      style="max-width: 240px"
                      class="ma-0 pa-0"
                      v-model="selectedRequest.status"
                      :items="reviewOptions"
                      filled
                      outlined
                      dense
                      rounded
                      :hide-details="true"
                    ></v-select>
                  </v-row>
                  <v-row
                    justify="center"
                    justify-md="start"
                    v-if="selectedRequest.status === 'denied'"
                    class="ma-0 mt-8"
                  >
                    <v-textarea
                      style="max-width: 240px"
                      v-model="selectedRequest.reason"
                      label="Reason for Denial"
                      auto-grow
                      outlined
                      filled
                      clearable
                      rounded
                      rows="2"
                      :rules="[(v) => !!v || 'Reason is required']"
                    ></v-textarea>
                  </v-row>
                  <v-row justify="center" justify-md="start" class="ma-0">
                    <v-btn
                      @click="updateStatus()"
                      color="#e022d1"
                      dark
                      rounded
                      width="240px"
                      class="pink-btn mt-4"
                    >Update Status</v-btn>
                  </v-row>
                </div>
              </v-form>
            </div>
          </div>
        </div>
      </v-row>
    </div>

    <v-row justify="center">
      <v-overlay :value="loading">
        <v-progress-circular :size="100" color="#4390c0" indeterminate />
      </v-overlay>
    </v-row>
    <v-dialog v-model="promptDialog" max-width="480">
      <v-card class="pa-8" elevation="4" shaped style="border-radius: 24px">
        <v-card-text class="text-center">
          <v-icon size="96" color="red" v-if="promptType == 'error'">mdi-alert-circle</v-icon>
          <v-icon size="96" color="green" v-if="promptType == 'success'">mdi-check-circle</v-icon>
          <div class="text-h5 my-6" v-if="promptType == 'error'">Error</div>
          <div class="text-h5 my-6" v-if="promptType == 'success'">Success</div>
          <div class="body-1 text-h6">{{ promptMessage }}</div>
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn
            color="black"
            dark
            depressed
            large
            style="border-radius: 24px"
            @click="promptDialog = false"
          >CLOSE</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deletingAd" max-width="480">
      <v-card class="pa-8" elevation="4" shaped style="border-radius: 24px">
        <v-card-text class="text-center">
          <v-icon size="96" color="green">mdi-check-circle</v-icon>
          <div class="text-h5 my-6">Delete Ad</div>
          <div class="body-1 text-h6">Are you sure you want to delete this ad?</div>
        </v-card-text>
        <v-row v-if="selectedRequest" justify="center" justify-md="center" class="ma-0">
          <v-textarea
            v-model="selectedRequest.deleteReason"
            label="Reason for deletion"
            auto-grow
            outlined
            filled
            clearable
            rounded
            rows="2"
            :rules="[(v) => !!v || 'Reason is required']"
            dense
          ></v-textarea>
        </v-row>
        <v-card-actions class="justify-center">
          <v-btn
            color="black"
            dark
            depressed
            large
            style="border-radius: 24px"
            @click="deletingAd = false"
          >Cancel</v-btn>
          <v-btn
            color="#4390c0"
            dark
            depressed
            large
            style="border-radius: 24px"
            @click="deleteAd()"
          >Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-col>
</template>

<script>
import { Browser } from "@capacitor/browser";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";
import {
  collection,
  getDocs,
  getFirestore,
  setDoc,
  query,
  updateDoc,
  doc,
  onSnapshot,
  addDoc,
  Timestamp,
  deleteDoc
} from "firebase/firestore";
import { firebaseApp } from "../../firebaseConfig";
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

import { logAction, sendPushNotificationUser } from "@/utils/firestoreUtil";

export default {
  data() {
    return {
      selectedDoctor: null,
      selectedDuration: null,
      adDurations: [
        { label: "7 Days", value: 7 },
        { label: "14 Days", value: 14 },
        { label: "30 Days", value: 30 },
        { label: "60 Days", value: 60 }
      ],
      freeAds: 0,
      adToCreate: null,
      selectedAd: null,
      numberRules: [v => v >= 0 || "Must be positive"],
      adTitle: "",
      ads: [],
      adImage: null,
      previewURL: null,
      creatingAd: false,
      headers: [
        { text: "Title", value: "title" },
        {
          text: "Date Created",
          value: "date"
        },
        { text: "Doctor", value: "doctorFullName" },
        { text: "Email", value: "doctorEmail" },
        { text: "Expiry Date", value: "expiry" },
        { text: "Status", value: "status" },
        { text: "Action", value: "action" }
      ],
      search: "",
      statusTypes: ["pending", "reviewed", "approved", "denied"],
      reviewOptions: ["approved", "denied"],
      selectedType: "pending",
      requests: [],
      selectedRequest: null,
      viewingRequest: false,
      loading: false,
      promptDialog: false,
      promptMessage: "",
      promptType: "",
      doctors: [],
      unsubscribeAdsRequests: null,
      unsubscribeDoctors: null,
      submittingAd: false,
      adReview: {},
      nullRules: [v => !!v || "Field is required"],
      deletingAd: false
    };
  },
  methods: {
    async checkAndExpireAds() {
      const snapshot = await getDocs(collection(db, "ads"));
      const now = new Date();

      for (const docSnap of snapshot.docs) {
        const ad = docSnap.data();
        const expiry = ad.expiryDate.toDate();

        if (ad.status === "approved" && expiry < now) {
          await updateDoc(doc(db, "ads", docSnap.id), { status: "expired" });
          console.log(`Ad ${ad.title} marked as expired.`);
        }
      }
    },
    async getActiveAds() {
      const q = query(collection(db, "ads"));
      const snapshot = await getDocs(q);
      const now = new Date();

      this.activeAds = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(ad => ad.status === "approved" && ad.expiryDate.toDate() > now);
    },
    getFutureDateSameTime(days) {
      const future = new Date();
      future.setDate(future.getDate() + days);
      return future;
    },
    async createNewAdd() {
      if (!this.adTitle) {
        this.promptMessage = "Please enter a title for the ad!";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      if (!this.selectedDuration) {
        this.promptMessage = "Please select an ad duration!";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      this.loading = true;
      let url, newRef;

      // Upload image if selected
      if (this.adImage) {
        newRef = ref(
          storage,
          `Ads/${this.loggedInUser.id}/Ad_${Date.now()}.png`
        );
        await uploadBytes(newRef, this.adImage);
        url = await getDownloadURL(newRef);
      }

      // Calculate expiry date
      const expiryDate = this.getFutureDateSameTime(this.selectedDuration);

      // Build ad object
      if (!this.selectedDoctor) {
        this.promptMessage = "Please select a doctor!";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      const newAd = {
        title: this.adTitle,
        days: this.selectedDuration,
        dateCreated: Timestamp.fromDate(new Date()),
        expiryDate: Timestamp.fromDate(expiryDate),
        doctorID: this.selectedDoctor, // <-- store selected doctor
        status: "pending"
      };

      if (this.adImage) {
        newAd.imageUrl = url;
        newAd.imageRef = newRef.fullPath;
      }

      try {
        await addDoc(collection(db, "ads"), newAd);
        this.loading = false;

        this.promptMessage = "Successfully created ad!";
        this.promptType = "success";
        this.promptDialog = true;

        setTimeout(() => {
          this.creatingAd = false;
          this.getRequests();
        }, 2000);
      } catch (error) {
        console.error("Error: ", error);
        this.loading = false;
        this.promptMessage = "Something went wrong while creating the ad!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async handleAdFromLocal(freeAd = false) {
      let saved = localStorage.getItem("SavedAd");
      if (!saved) return;

      let newAd = JSON.parse(saved);

      // Convert string dates back to Date objects
      if (typeof newAd.dateCreated === "string")
        newAd.dateCreated = new Date(newAd.dateCreated);
      if (typeof newAd.expiryDate === "string")
        newAd.expiryDate = new Date(newAd.expiryDate);

      try {
        this.loading = true;
        await addDoc(collection(db, "ads"), newAd);
        this.loading = false;

        if (freeAd) this.removeFreeAdFromUser();

        localStorage.removeItem("SavedAd");

        this.promptMessage = "Successfully created ad!";
        this.promptType = "success";
        this.promptDialog = true;

        setTimeout(() => {
          this.creatingAd = false;
          this.getRequests(); // Refresh list
        }, 2000);
      } catch (error) {
        console.log("Error: ", error);
        this.loading = false;
        this.promptMessage = "Something went wrong while creating the ad!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async removeFreeAdFromUser() {
      let subscription = this.loggedInUser.subscription;
      if (subscription.freeAdsLeft && subscription.freeAdsLeft > 0)
        subscription.freeAdsLeft--;

      try {
        this.loading = true;
        await updateDoc(doc(db, "doctors", this.loggedInUser.id), {
          subscription
        });
        this.loading = false;
      } catch (error) {
        console.log("Error updating free ads: ", error);
        this.loading = false;
      }
    },
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.adImage = file;
        this.previewURL = URL.createObjectURL(file);
      }
    },
    close() {
      this.viewingRequest = false;
      localStorage.removeItem("ViewedRequest");
    },
    viewRequest(item) {
      this.selectedRequest = item;
      this.viewingRequest = true;

      if (this.selectedRequest.reviewed) {
        this.adReview = { ...this.selectedRequest };
      }

      localStorage.setItem(
        "ViewedRequest",
        JSON.stringify(this.selectedRequest)
      );
    },
    enterCreateMode() {
      this.creatingAd = true;
      this.selectedRequest = null;
    },
    getRequests() {
      try {
        const q = query(collection(db, "ads"));

        this.loading = true;

        // Subscribe to real-time updates
        this.unsubscribeAdsRequests = onSnapshot(q, querySnapshot => {
          this.requests = [];

          querySnapshot.forEach(doc => {
            let tempRequest = doc.data();
            tempRequest.id = doc.id;

            console.log(
              "Doctors loaded:",
              this.doctors.map(d => d.id)
            );
            console.log("Ad doctorID:", tempRequest.doctorID);

            // Find matching doctor
            let doctor = this.doctors.find(d => d.id === tempRequest.doctorID);

            // Always define the flattened doctor info safely
            if (doctor) {
              tempRequest.doctorFullName = `${doctor.name} ${doctor.surname}`;
              tempRequest.doctorEmail = doctor.email || "N/A";
            } else {
              tempRequest.doctorFullName = "Unknown";
              tempRequest.doctorEmail = "N/A";
            }

            // Format dates
            if (tempRequest.dateCreated?.seconds)
              tempRequest.date =
                new Date(tempRequest.dateCreated.seconds * 1000)
                  .toISOString()
                  .substring(0, 10) +
                " " +
                new Date(
                  tempRequest.dateCreated.seconds * 1000
                ).toLocaleTimeString();

            if (tempRequest.expiryDate?.seconds)
              tempRequest.expiry =
                new Date(tempRequest.expiryDate.seconds * 1000)
                  .toISOString()
                  .substring(0, 10) +
                " " +
                new Date(
                  tempRequest.expiryDate.seconds * 1000
                ).toLocaleTimeString();

            // Default status
            if (!tempRequest.status) tempRequest.status = "pending";

            this.requests.push(tempRequest);
          });

          this.loading = false;
        });
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
      }
    },
    statusColor(status) {
      switch (status) {
        case "denied":
          return "#e022d1";
        case "pending":
          return "#4390c0";
        case "approved":
          return "green";
        default:
          return "grey";
      }
    },
    getDoctors() {
      try {
        this.loading = true;
        const q = query(collection(db, "doctors"));

        // Subscribe to real-time updates
        this.unsubscribeDoctors = onSnapshot(q, querySnapshot => {
          this.doctors = querySnapshot.docs.map(doctor => ({
            id: doctor.id,
            ...doctor.data()
          }));

          this.loading = false;
        });
      } catch (error) {
        console.log("Error: ", error);
        this.loading = false;
        this.promptMessage = "An error occurred while fetching the doctors!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async submitReview() {
      let valid = this.$refs.reviewForm.validate();

      if (!valid) {
        this.promptMessage = "Please fill out all required fields";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      this.selectedRequest.status = "reviewed";
      this.selectedRequest.reviewed = true;
      this.selectedRequest.reviewedBy =
        this.loggedInUser.name + " " + this.loggedInUser.surname;

      let newRequest = {
        ...this.selectedRequest,
        ...this.adReview
      };

      try {
        this.loading = true;
        await setDoc(doc(db, "ads", this.selectedRequest.id), newRequest);
        this.loading = false;

        this.promptMessage = "Successfully submitted ad review!";
        this.promptType = "success";
        this.promptDialog = true;
      } catch (error) {
        this.loading = false;
        console.log("Error: ", error);
        this.promptMessage =
          "Something went wrong while submitting your review, please try again!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async updateStatus() {
      try {
        if (
          this.selectedRequest.status === "denied" &&
          !this.selectedRequest.reason
        ) {
          this.promptMessage = "Please provide a reason for denial.";
          this.promptType = "error";
          this.promptDialog = true;
          return;
        }

        this.loading = true;

        await setDoc(doc(db, "ads", this.selectedRequest.id), {
          ...this.selectedRequest,
          // Save reason only if denied, otherwise clear it
          reason:
            this.selectedRequest.status === "denied"
              ? this.selectedRequest.reason
              : ""
        });

        this.loading = false;

        if (this.selectedRequest.status == "denied") {
          const doctor = this.doctors.find(
            d => d.id === this.selectedRequest.doctorID
          );
          if (doctor?.fcmToken) {
            await sendPushNotificationUser(
              "Ad Denied",
              `Your ad for ${this.selectedRequest.title} has been declined for the following reason: ${this.selectedRequest.reason}`,
              doctor.fcmToken
            );
          }
          this.addNotification(
            this.selectedRequest.doctorID,
            "Ad Denied",
            "Your ad for " +
              this.selectedRequest.title +
              " has been declined for the following reason: " +
              this.selectedRequest.reason
          );
        } else if (this.selectedRequest.status == "approved") {
          const doctor = this.doctors.find(
            d => d.id === this.selectedRequest.doctorID
          );
          if (doctor?.fcmToken) {
            await sendPushNotificationUser(
              "Ad Approved",
              `Your ad for ${this.selectedRequest.title} has been approved`,
              doctor.fcmToken
            );
          }

          this.addNotification(
            this.selectedRequest.doctorID,
            "Ad Approved",
            "Your ad for " + this.selectedRequest.title + " has been approved"
          );
        }

        await this.logStatusUpdate();

        this.promptMessage = "Successfully updated ad status!";
        this.promptType = "success";
        this.promptDialog = true;

        setTimeout(() => {
          this.promptDialog = false;
        }, 3000);
      } catch (error) {
        this.loading = false;
        console.log("error: ", error);
        this.promptMessage =
          "An error occured while updating the ad's status, please try again!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    },
    async logStatusUpdate() {
      await logAction(
        "Ad Management",
        "Status Update",
        this.loggedInUser.email,
        `${this.loggedInUser.email} has updated the status of ad ${
          this.selectedRequest.id
        } to ${this.selectedRequest.status}. Reason: ${this.selectedRequest
          .reason || "N/A"}`
      );
    },
    async addNotification(userID, type, message) {
      let notification = {
        createdAt: new Date(),
        userType: "Doctor",
        userID: userID,
        type: type,
        message: message
      };

      try {
        this.loading = true;
        let res2 = await addDoc(collection(db, "alerts"), notification);
        this.loading = false;
      } catch (error) {
        console.log("Error: ", error);
        this.loading = false;
      }
    },
    async deleteAd() {
      if (!this.selectedRequest.deleteReason || this.selectedRequest.deleteReason && this.selectedRequest.deleteReason.length < 4) {
        this.promptMessage = "Please provide a reason for deletion!";
        this.promptType = "error";
        this.promptDialog = true;
        return;
      }

      this.deletingAd = false;

      try {
        this.loading = true;

        let reason = this.selectedRequest.deleteReason;

        if (this.selectedRequest.imageRef) {
          let newRef = ref(storage, this.selectedRequest.imageRef);
          await deleteObject(newRef);
        }

        await deleteDoc(doc(db, "ads", this.selectedRequest.id));
        this.loading = false;

        const doctor = this.doctors.find(
          d => d.id === this.selectedRequest.doctorID
        );

        if (doctor?.fcmToken) {
          await sendPushNotificationUser(
            "Ad Deleted",
            `Your ad for ${this.selectedRequest.title} has been deleted for the following reason: ${reason}`,
            doctor.fcmToken
          );
        }

        this.addNotification(
          this.selectedRequest.doctorID,
          "Ad Deleted",
          "Your ad for " +
            this.selectedRequest.title +
            " has been deleted for the following reason: " +
            reason
        );

        this.promptMessage = "Successfully deleted ad!";
        this.promptType = "success";
        this.promptDialog = true;
      } catch (error) {
        this.loading = false;
        console.log("Storage Delete Doc Error: ", error);
        this.promptMessage =
          "Something went wrong while deleting the ad, please try again!";
        this.promptType = "error";
        this.promptDialog = true;
      }
    }
  },
  async created() {
    console.log("Logged in user:", this.loggedInUser);
    let res = await this.getDoctors();
    let res2 = await this.getRequests();

    if (localStorage.getItem("ViewedRequest")) {
      let selectedRequest = JSON.parse(localStorage.getItem("ViewedRequest"));
      let newRequest = this.requests.find(
        request => request.id == selectedRequest.id
      );

      if (newRequest) {
        this.viewRequest(newRequest);
      }
    }
  },
  computed: {
    filteredRequests() {
      return this.requests
        .filter(item => {
          // Filter by status if selectedStatus is not null
          const matchesStatus =
            this.selectedType === null || item.status === this.selectedType;

          // Filter by search text
          const matchesSearch = Object.values(item).some(value => {
            if (value !== null && value !== undefined) {
              return value
                .toString()
                .toLowerCase()
                .includes(this.search.toLowerCase());
            }
            return false;
          });

          // Only return items that match both filters
          return matchesStatus && matchesSearch;
        })
        .sort(
          (a, b) =>
            new Date(b.dateCreated.seconds * 1000) -
            new Date(a.dateCreated.seconds * 1000)
        );
    },
    loggedInUser() {
      return this.$store.state.user || {};
    },
    role() {
      return this.loggedInUser.adminType;
    }
  },
  beforeUnmount() {
    if (this.unsubscribeAdsRequests) {
      this.unsubscribeAdsRequests();
    }

    if (this.unsubscribeDoctors) {
      this.unsubscribeDoctors();
    }
  }
};
</script>

<style scoped>
.reason-field {
  background-color: #fafafa;
  border-radius: 12px;
}

.image {
  width: 200px;
  border: 2px solid grey;
}
</style>
